import { JsonMap } from '@segment/analytics-react-native';
import { Link, useRouter } from 'expo-router';

import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { segmentClient } from '@/util/analytics';
import { Sentry } from '@/util/sentry';

type AnalyticsEventKey = keyof typeof ANALYTICS_EVENTS;

type AnalyticsEventValue = (typeof ANALYTICS_EVENTS)[AnalyticsEventKey];

type LinkProps = React.ComponentProps<typeof Link>;

/**
 * TrackedLink Component
 *
 * A wrapper around expo-router's Link component that automatically tracks
 * analytics events when the link is pressed. Designed to decouple analytics
 * tracking from navigation logic and provide a consistent, declarative API.
 *
 * Behavior:
 * - Fires analytics event via segmentClient on press before navigation
 * - Preserves all standard Link props and behavior
 * - Calls any provided onPress handler after tracking
 * - Falls back to navigate() if no onPress handler is provided
 * - Captures tracking failures to Sentry without blocking navigation
 *
 * Example:
 * ```tsx
 * <TrackedLink
 *   href="/inquiry"
 *   trackingEvent={ANALYTICS_EVENTS.SEND_INQUIRY_CLICKED}
 *   trackingData={{ source: 'web_navbar' }}
 *   asChild
 * >
 *   <Button>Send Inquiry</Button>
 * </TrackedLink>
 * ```
 */

type TrackedLinkProps = LinkProps & {
  /** The analytics event name to track when the link is pressed */
  trackingEvent: AnalyticsEventValue;
  /** Additional properties to include with the analytics event */
  trackingData?: JsonMap;
};

export const TrackedLink = ({
  trackingEvent,
  trackingData,
  href,
  onPress,
  children,
  ...props
}: TrackedLinkProps) => {
  const { navigate } = useRouter();

  const handlePress: LinkProps['onPress'] = (e) => {
    e.preventDefault();

    try {
      segmentClient.track(trackingEvent, trackingData);
    } catch (error) {
      Sentry.captureException(error, {
        level: 'warning',
        extra: {
          trackingEvent,
          trackingData,
          href,
        },
      });
    } finally {
      if (onPress) {
        onPress?.(e);
      } else {
        navigate(href);
      }
    }
  };

  return (
    <Link {...props} href={href} onPress={handlePress}>
      {children}
    </Link>
  );
};
